/**
 * Canonical registry of client-portal sidebar menus.
 *
 * Single source of truth shared by:
 *   • ClientLayout — gates each nav item (`vis(key)` against the venue's
 *     `hidden_menus`), on top of the existing provider/role gating.
 *   • AdminVenueDetail "Portal Access" section — renders a visibility toggle
 *     per hideable menu so platform admins can hide what isn't relevant for a
 *     given venue (e.g. a venue that doesn't issue digital membership).
 *
 * Keys here MUST match the keys passed to `vis(...)` in ClientLayout.
 *
 * Item shape:
 *   key        unique, stable id stored in venues.hidden_menus
 *   label      display name (matches the sidebar label)
 *   group      "main" | "reports" | "settings" (for grouping the toggles)
 *   providers  array of provider_types this item applies to; omit = all
 *   adminOnly  only shown to client admins (mirrors the sidebar)
 *   core       always visible — not hideable, excluded from the admin toggles
 *   feature    coupled to a dedicated feature flag (digital membership); these
 *              have their own toggle, so they're excluded from Portal Access
 */
export const CLIENT_MENU_ITEMS = [
  // ── Main ──
  { key: "dashboard",          label: "Dashboard",            group: "main", core: true },
  { key: "members",            label: "Members",              group: "main", core: true },
  { key: "s7000.pin-lookup",   label: "PIN Lookup",           group: "main", providers: ["s7000"] },
  { key: "s7000.reset-pin",    label: "Reset PIN",            group: "main", providers: ["s7000"] },
  { key: "igt.find-member",    label: "Find Member",          group: "main", providers: ["igt"], adminOnly: true },
  { key: "igt.verify-pin",     label: "Verify PIN",           group: "main", providers: ["igt"], adminOnly: true },
  { key: "igt.change-pin",     label: "Change PIN",           group: "main", providers: ["igt"], adminOnly: true },
  { key: "igt.update-profile", label: "Update Profile",       group: "main", providers: ["igt"], adminOnly: true },
  { key: "passes",             label: "Digital Passes",       group: "main", feature: "digital_passes" },
  { key: "s7000.live",         label: "Live Floor",           group: "main", providers: ["s7000"] },
  { key: "s7000.bulk-points",  label: "Bulk Points",          group: "main", providers: ["s7000"], adminOnly: true },

  // ── Reports ──
  { key: "reports.expiring",       label: "Memberships Expiring", group: "reports" },
  { key: "reports.cards-expiring", label: "Cards Expiring",       group: "reports" },
  { key: "reports.acquisition",    label: "Acquisition",          group: "reports", adminOnly: true },
  { key: "s7000.data-quality",     label: "Data Quality",         group: "reports", providers: ["s7000"], adminOnly: true },
  { key: "s7000.tag-coverage",     label: "Tag Coverage",         group: "reports", providers: ["s7000"], adminOnly: true },
  { key: "s7000.demographics",     label: "Demographics",         group: "reports", providers: ["s7000"], adminOnly: true },
  { key: "s7000.sync-awareness",   label: "Sync Awareness",       group: "reports", providers: ["s7000"], adminOnly: true },
  { key: "s7000.duplicates",       label: "Duplicates",           group: "reports", providers: ["s7000"], adminOnly: true },
  { key: "logs",                   label: "Activity Logs",        group: "reports" },
  { key: "products",               label: "Products",             group: "reports" },

  // ── Settings ──
  { key: "settings.profile",        label: "My Profile",      group: "settings", core: true },
  { key: "settings.portal",         label: "Portal",          group: "settings", adminOnly: true },
  { key: "settings.branding",       label: "Branding",        group: "settings", adminOnly: true },
  { key: "settings.emails",         label: "Emails",          group: "settings", adminOnly: true },
  { key: "settings.analysis-codes", label: "Analysis Codes",  group: "settings", providers: ["s7000"], adminOnly: true },
  { key: "settings.digital-card",   label: "Digital Card",    group: "settings", adminOnly: true, feature: "digital_passes" },
  { key: "settings.terms",          label: "T&C",             group: "settings", adminOnly: true },
  { key: "settings.communications", label: "Comms",           group: "settings", adminOnly: true },
  { key: "settings.staff",          label: "Staff",           group: "settings", adminOnly: true },
];

export const MENU_GROUP_LABELS = {
  main:     "Main",
  reports:  "Reports",
  settings: "Settings",
};

/**
 * Menus eligible for the admin "Portal Access" visibility toggles, filtered to
 * a venue's provider. Excludes core items (always shown) and feature-coupled
 * items (digital membership has its own dedicated toggle).
 */
export function hideableMenusForProvider(providerType) {
  // Use the provider as-is so a non-s7000/igt provider (e.g. swiftpos) only
  // gets the provider-agnostic hideable menus — provider-specific items
  // (providers: ["s7000"]/["igt"]) are excluded because they never render in
  // that venue's sidebar. Default a missing provider_type to s7000.
  const pt = providerType || "s7000";
  return CLIENT_MENU_ITEMS.filter(
    (m) => !m.core && !m.feature && (!m.providers || m.providers.includes(pt))
  );
}
